// stores/projects.js
import { defineStore } from 'pinia';
import { fetchProjects } from '@/api';
import { StorageManager, cacheKeys } from '@/utils/cache';
import { loadCriticalImages, preloadAdjacentProjects } from '@/utils/projectsLoader';
import { sortProjectsByDate } from '@/api';

export const useProjectStore = defineStore('projectStore', {
    state: () => ({
        projects: null,
        visibleProjects: [],
        cachedThumbnails: {},
        loading: false,
        error: null,
        criticalLoadingProjects: new Set(),
        projectsWithLoadedCriticalImages: new Set(),
        loadedProjects: new Set()
    }),

    getters: {
        sortedProjects: (state) => {
            if (!state.projects) return [];
            return sortProjectsByDate(state.projects);
        },

        isProjectLoading: (state) => (projectId) => {
            if (!projectId) return false;
            return state.criticalLoadingProjects.has(projectId);
        },

        isProjectLoaded: (state) => (projectId) => {
            if (!projectId) return false;
            return state.loadedProjects.has(projectId);
        }
    },

    actions: {
        async loadProjects() {
            if (this.projects) {
                return this.projects;
            }

            this.loading = true;
            this.error = null;

            try {
                let cachedData = null;
                try {
                    cachedData = localStorage.getItem(cacheKeys.PROJECTS);
                    if (cachedData) {
                        cachedData = JSON.parse(cachedData);
                    }
                } catch (e) {
                    console.warn('Error parsing cached projects:', e);
                    cachedData = null;
                }

                if (cachedData && cachedData.data && cachedData.timestamp) {
                    if (Date.now() - cachedData.timestamp < 86400000) {
                        this.projects = cachedData.data;
                        this.initVisibleProjects();
                        return this.projects;
                    }
                }

                // fetchProjects() writes cacheKeys.PROJECTS itself, so there is
                // deliberately no second write here - a duplicate copy under a
                // different key is what previously made clearCache() ineffective.
                this.projects = await fetchProjects();

                this.initVisibleProjects();

                return this.projects;
            } catch (error) {
                this.error = error.message;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        markProjectLoaded(projectId) {
            if (projectId) {
                this.loadedProjects.add(projectId);
            }
        },

        // Reset the loaded state for a specific project (used when navigating back)
        resetProjectLoadedState(projectId) {
            if (projectId && this.loadedProjects.has(projectId)) {
                this.loadedProjects.delete(projectId);
            }
        },

        initVisibleProjects() {
            if (this.projects && this.projects.length > 0) {
                const sorted = sortProjectsByDate(this.projects);
                this.visibleProjects = sorted.slice(0, 1);
            }
        },

        loadMoreProjects(batchSize = 1) {
            if (!this.projects || this.visibleProjects.length >= this.projects.length) {
                return;
            }

            const sorted = sortProjectsByDate(this.projects);
            const currentCount = this.visibleProjects.length;
            const nextBatch = sorted.slice(currentCount, currentCount + batchSize);

            this.visibleProjects = [...this.visibleProjects, ...nextBatch];

            return nextBatch;
        },

        ensureProjectVisible(projectId) {
            if (this.visibleProjects.some(p => p.id === projectId)) {
                return;
            }

            const targetProject = this.projects.find(p => p.id === projectId);
            if (!targetProject) return;

            this.visibleProjects = [...this.visibleProjects, targetProject];
        },

        async loadCriticalImages(project, imageIndex) {
            if (!project || !project.id) {
                console.warn('Invalid project passed to loadCriticalImages');
                return;
            }

            if (this.projectsWithLoadedCriticalImages.has(project.id)) {
                return;
            }

            this.criticalLoadingProjects.add(project.id);

            try {
                this.ensureProjectVisible(project.id);

                await loadCriticalImages(project, imageIndex);

                this.projectsWithLoadedCriticalImages.add(project.id);
                // Also mark as generally loaded
                this.markProjectLoaded(project.id);

                if (project.attributes?.images?.data) {
                    this.cacheThumbnails(project.id, project.attributes.images.data);
                }
            } catch (error) {
                console.error('Error loading critical images:', error);
            } finally {
                this.criticalLoadingProjects.delete(project.id);
            }
        },

        async preloadAdjacentProject(direction, currentProjectId) {
            if (!this.projects || !currentProjectId) return;

            const sorted = sortProjectsByDate(this.projects);
            const currentIndex = sorted.findIndex(p => p.id === currentProjectId);

            if (currentIndex === -1) return;

            let targetIndex;
            if (direction === 'next') {
                targetIndex = currentIndex + 1;
                if (targetIndex >= sorted.length) {
                    targetIndex = 0;
                }
            } else {
                targetIndex = currentIndex - 1;
                if (targetIndex < 0) {
                    targetIndex = sorted.length - 1;
                }
            }

            const targetProject = sorted[targetIndex];
            if (!targetProject) return;

            if (!this.visibleProjects.some(p => p.id === targetProject.id)) {
                this.visibleProjects = [...this.visibleProjects, targetProject];
            }

            const imageIndex = direction === 'next' ? 0 :
                (targetProject.attributes?.images?.data?.length - 1) || 0;

            return this.loadCriticalImages(targetProject, imageIndex);
        },

        clearCache() {
            // Get the IDs of the first 5 projects before clearing
            const firstFiveProjects = this.sortedProjects.slice(0, 5).map(p => p.id);

            // Save the thumbnail data for these projects
            const thumbnailsToPreserve = {
                visibleImages: {},
                loadingStatus: {}
            };

            if (window.thumbnailCache) {
                firstFiveProjects.forEach(projectId => {
                    if (window.thumbnailCache.visibleImages[projectId]) {
                        thumbnailsToPreserve.visibleImages[projectId] =
                            window.thumbnailCache.visibleImages[projectId];
                    }
                    if (window.thumbnailCache.loadingStatus[projectId]) {
                        thumbnailsToPreserve.loadingStatus[projectId] =
                            window.thumbnailCache.loadingStatus[projectId];
                    }
                });
            }

            // Clear projects but keep thumbnails for first 5
            this.projects = null;
            this.visibleProjects = [];
            this.cachedThumbnails = {};
            this.projectsWithLoadedCriticalImages.clear();

            // Don't clear first 5 projects from loadedProjects
            firstFiveProjects.forEach(projectId => {
                if (this.loadedProjects.has(projectId)) {
                    // Keep this project loaded
                } else {
                    this.loadedProjects.delete(projectId);
                }
            });

            localStorage.removeItem(cacheKeys.PROJECTS);

            // Keep the thumb cache for first 5 projects
            if (window.thumbnailCache) {
                window.thumbnailCache.visibleImages = thumbnailsToPreserve.visibleImages;
                window.thumbnailCache.loadingStatus = thumbnailsToPreserve.loadingStatus;
            }

            if (StorageManager && typeof StorageManager.clear === 'function') {
                StorageManager.clear(cacheKeys.PROJECTS);
            }
        },

        cacheThumbnails(projectId, thumbnails) {
            this.cachedThumbnails[projectId] = thumbnails;
        },

        getCachedThumbnails(projectId) {
            return this.cachedThumbnails[projectId] || null;
        }
    }
});